import { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectActivePokemon } from '../ActiveGame/selectors';
import { anchors } from '../Motion/anchorNames';
import { justDropped } from '../Drag/dropTargets';

const SLIDE = 'transform .32s cubic-bezier(.2, .7, .2, 1)';

/** The element drawn at a place on the board (the anchor itself has no box). */
const at = (name) => document.querySelector(`[data-anchor="${name}"] > *`);

/** What moves `from` onto `to`: across, and grown or shrunk to its size. */
function onto(from, to) {
  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  return `translate(${dx}px, ${dy}px) scale(${b.width / a.width})`;
}

/**
 * A retreat or Switch waiting for its Undo, shown done: the active Pokémon and the benched one trade places on the
 * board right away (moved there, not yet sent). Undo slides them back; once the game answers with them really
 * traded, the board draws them there and nothing needs moving.
 */
export default function SwapPreview() {
  const swap = useSelector((state) => (state.undo ? state.undo.swap : null));
  const active = useSelector((state) => (swap ? selectActivePokemon(state, { userId: swap.userId }) : null));
  const activeId = active ? active.get('id') : null;
  const moved = useRef(null);

  useLayoutEffect(() => {
    const previous = moved.current;
    // The game answered (a different active Pokémon is drawn) or the swap is over: put things back as they are.
    if (previous && (!swap || previous.activeId !== activeId)) {
      const answered = previous.activeId !== activeId;
      previous.elements.forEach((element) => {
        element.style.transition = answered ? 'none' : SLIDE;
        element.style.transform = '';
        element.style.zIndex = '';
      });
      moved.current = null;
    }
    if (!swap || moved.current) {
      return;
    }
    const up = at(anchors.bench(swap.userId, swap.benchPosition));
    const down = at(anchors.active(swap.userId));
    if (!up || !down) {
      return;
    }
    const [toActive, toBench] = [onto(up, down), onto(down, up)];
    // The Pokémon you dragged there is already where you let it go: it lands, it doesn't slide there again.
    const dropped = justDropped(anchors.active(swap.userId));
    [[up, toActive, false], [down, toBench, dropped]].forEach(([element, transform, landed]) => {
      element.style.transition = landed ? 'none' : SLIDE;
      element.style.transform = transform;
      element.style.position = element.style.position || 'relative';
      element.style.zIndex = '6';
    });
    moved.current = { activeId, elements: [up, down] };
  }, [swap, activeId]);

  return null;
}
